import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as api from "../../utils/api";
import PosterModal from "../../components/modals/PosterModal";

const SORT_OPTIONS = [
  { value: "created_at_desc", label: "Created (Newest first)" },
  { value: "created_at_asc", label: "Created (Oldest first)" },
  { value: "name_asc", label: "Name (A-Z)" },
  { value: "name_desc", label: "Name (Z-A)" },
  { value: "orientation_asc", label: "Orientation (A-Z)" },
  { value: "orientation_desc", label: "Orientation (Z-A)" },
];

const Posters = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [systemFonts, setSystemFonts] = useState([]);
  const [posterPresets, setPosterPresets] = useState([]);

  const [metaPage, setMetaPage] = useState(0);
  const [metaTotalPages, setMetaTotalPages] = useState(0);
  const [metaTotalCount, setMetaTotalCount] = useState(0);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [orientationFilter, setOrientationFilter] = useState("");
  const [orientationQuery, setOrientationQuery] = useState("");

  const [modal, setModal] = useState({ open: false, mode: "create", poster: null });

  const openCreate = useCallback(() => {
    setModal({ open: true, mode: "create", poster: null });
  }, []);
  const openEdit = useCallback((poster) => {
    setModal({ open: true, mode: "edit", poster });
  }, []);
  const closeModal = useCallback(() => {
    setModal({ open: false, mode: "create", poster: null });
  }, []);

  const loadSystemFonts = useCallback(async () => {
    const arr = await api.endpoints.getSystemFonts();
    arr.sort((a, b) => (a?.name || "").localeCompare(b?.name || "", undefined, { sensitivity: "base" }));
    setSystemFonts(arr);
  }, []);

  const loadPosterPresets = useCallback(async () => {
    const arr = await api.endpoints.getPosterPresets();
    arr.sort((a, b) => (a?.name || "").localeCompare(b?.name || "", undefined, { sensitivity: "base" }));
    setPosterPresets(arr);
  }, []);

  const loadPosters = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.endpoints.fetchPosters({
        page,
        per_page: perPage,
        sort_by: sortBy,
        sort_dir: sortDir,
        q: searchQuery,
        orientation: orientationQuery,
      });

      const data = Array.isArray(res) ? res : (Array.isArray(res?.poster_options) ? res.poster_options : (Array.isArray(res?.data) ? res.data : []));
      const mapped = data.map((p) => ({
        id: p.id,
        name: p.name || "-",
        orientation: p.orientation || "-",
        color1: p.color1 || "#ffffff",
        color2: p.color2 || "#ffffff",
        color3: p.color3 || "#ffffff",
        color_bkg: p.color_bkg || "#ffffff",
        pps_slideshow: !!p.pps_slideshow,
        video_slideshow: !!p.video_slideshow,
        slide_duration: p.slide_duration ?? 15,
        bar_logo_type: p.bar_logo_type || "-",
        example_url: p.example_url || p.example || "",
        _raw: p,
      }));

      setRows(mapped);
      setMetaPage(page);
      setMetaTotalPages(0);
      setMetaTotalCount(data.length);
    } catch (ex) {
      console.log(ex);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, sortBy, sortDir, searchQuery, orientationQuery]);

  useEffect(() => {
    loadSystemFonts();
    loadPosterPresets();
  }, [loadSystemFonts, loadPosterPresets]);

  useEffect(() => {
    loadPosters();
  }, [loadPosters]);

  const handleSearch = useCallback(() => {
    setSearchQuery(searchInput);
    setOrientationQuery(orientationFilter);
    setPage(1);
  }, [searchInput, orientationFilter]);

  const handleSavePoster = useCallback(
    async (payload, posterId = null) => {
      const fd = new FormData();
      fd.append("poster[name]", payload.name ?? "");
      if (payload.orientation) fd.append("poster[orientation]", payload.orientation);
      if (payload.color1) fd.append("poster[color1]", payload.color1);
      if (payload.color2) fd.append("poster[color2]", payload.color2);
      if (payload.color3) fd.append("poster[color3]", payload.color3);
      if (payload.color_bkg) fd.append("poster[color_bkg]", payload.color_bkg);
      fd.append("poster[bar_logo_type]", payload.bar_logo_type || "transparent");
      fd.append("poster[pps_slideshow]", payload.pps_slideshow ? "true" : "false");
      fd.append("poster[video_slideshow]", payload.video_slideshow ? "true" : "false");
      if (payload.slide_duration != null) fd.append("poster[slide_duration]", String(payload.slide_duration));

      if (payload.system_font_id != null && payload.system_font_id !== "")
        fd.append("poster[system_font_id]", String(payload.system_font_id));
      if (payload.nr_of_columns != null) fd.append("poster[nr_of_columns]", String(payload.nr_of_columns));

      const numFields = [
        "font_size_primary", "font_size_secondary", "font_size_matchday",
        "font_size_date_day", "font_size_date_month", "font_size_title",
        "page_width", "page_height", "page_margin_side", "page_margin_side_extra",
        "page_margin_top", "page_margin_bottom", "logo_position_y", "logo_margin_top",
        "title_margin_top", "title_margin_bottom", "portion_width", "portion_width_single_column",
        "portion_line_height", "time_margin_right", "portion_vertical_spacing",
        "entry_margin_bottom", "matchday_margin_bottom", "entry_max_lines",
        "min_entries_for_split", "min_entries_per_column_split",
        "single_column_extra_margin", "max_zoom_multiplier",
        "favorite_star_stroke_width", "favorite_star_size", "favorite_star_gap",
      ];
      for (const key of numFields) {
        if (payload[key] !== undefined && payload[key] !== null && payload[key] !== "")
          fd.append(`poster[${key}]`, String(payload[key]));
      }

      if (payload.favorite_star_fill) fd.append("poster[favorite_star_fill]", payload.favorite_star_fill);
      if (payload.favorite_star_stroke) fd.append("poster[favorite_star_stroke]", payload.favorite_star_stroke);

      if (payload.bkg_image) fd.append("poster[bkg_image]", payload.bkg_image);
      if (payload.example) fd.append("poster[example]", payload.example);
      if (payload.logo_sc) fd.append("poster[logo_sc]", payload.logo_sc);
      if (payload.custom_font_file) fd.append("poster[custom_font_file]", payload.custom_font_file);

      if (posterId) {
        await api.endpoints.updatePoster(posterId, fd);
      } else {
        await api.endpoints.createPoster(fd);
      }
      await loadPosters();
      closeModal();
    },
    [loadPosters, closeModal]
  );

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortDir("asc");
    }
    setPage(1);
  };

  const thStyle = (key) => ({
    cursor: "pointer",
    userSelect: "none",
    whiteSpace: "nowrap",
    paddingRight: 8,
  });

  const sortIndicator = (key) => {
    if (sortBy !== key) return " ↕";
    return sortDir === "asc" ? " ↑" : " ↓";
  };

  return (
    <div className="d-flex bg-secondary w-75 h-100 ml-5 overflow-auto">
      <div className="w-100 mr-5 ml-2 bg-secondary" style={{ padding: "16px 0" }}>
        {/* Title bar */}
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h4 className="fs-h3 text--shadenavy mb-0">Posters</h4>
          <button className="btn btn-primary btn-sm" onClick={openCreate}>
            + Add new poster
          </button>
        </div>

        {/* Search */}
        <div className="card mb-3">
          <div className="card-body">
            <div className="d-flex flex-wrap align-items-end" style={{ gap: 12 }}>
              <div style={{ flex: 1, minWidth: 180 }}>
                <label className="d-block mb-1" style={{ fontSize: 13, fontWeight: 500 }}>Search posters</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Type at least 3 chars..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                />
              </div>
              <div style={{ minWidth: 160 }}>
                <label className="d-block mb-1" style={{ fontSize: 13, fontWeight: 500 }}>Orientation</label>
                <select
                  className="form-control form-control-sm"
                  value={orientationFilter}
                  onChange={(e) => setOrientationFilter(e.target.value)}
                >
                  <option value="">All orientations</option>
                  <option value="landscape">Landscape</option>
                  <option value="portrait">Portrait</option>
                  <option value="instagram">Instagram</option>
                </select>
              </div>
              <div style={{ minWidth: 160 }}>
                <label className="d-block mb-1" style={{ fontSize: 13, fontWeight: 500 }}>Sort by</label>
                <select
                  className="form-control form-control-sm"
                  value={`${sortBy}_${sortDir}`}
                  onChange={(e) => {
                    const v = e.target.value;
                    const i = v.lastIndexOf("_");
                    setSortBy(v.substring(0, i));
                    setSortDir(v.substring(i + 1));
                    setPage(1);
                  }}
                >
                  {SORT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <button className="btn btn-primary btn-sm" onClick={handleSearch}>
                Search
              </button>
            </div>
          </div>
        </div>

        {/* Table card */}
        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between">
            <span style={{ fontSize: 14, fontWeight: 600 }}>
              Page {metaPage || page} of {metaTotalPages || 1} &bull; {metaTotalCount} total
            </span>
            <div className="d-flex align-items-center" style={{ gap: 8 }}>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
              >
                &lsaquo; Prev
              </button>
              <span style={{ fontSize: 13 }}>Page</span>
              <input
                type="number"
                min={1}
                className="form-control form-control-sm"
                value={page}
                onChange={(e) => setPage(Math.max(1, Number(e.target.value) || 1))}
                style={{ width: 60, textAlign: "center" }}
              />
              <span style={{ fontSize: 13 }}>of {Math.max(metaTotalPages || 1, 1)}</span>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setPage((p) => Math.min(p + 1, Math.max(metaTotalPages || p + 1, p + 1)))}
                disabled={metaTotalPages ? page >= metaTotalPages : false}
              >
                Next &rsaquo;
              </button>
              <select
                className="form-control form-control-sm ml-2"
                value={perPage}
                onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
                style={{ width: 80 }}
              >
                <option value={20}>20</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span style={{ fontSize: 13 }}>per page</span>
            </div>
          </div>
          <div className="card-body p-0">
            {loading ? (
              <div className="p-4 text-center text-muted">Loading...</div>
            ) : rows.length === 0 ? (
              <div className="p-4 text-center text-muted">No posters found.</div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table className="table table-sm table-hover mb-0" style={{ fontSize: 13 }}>
                  <thead className="thead-light">
                    <tr>
                      <th style={thStyle("name")} onClick={() => handleSort("name")}>
                        NAME{sortIndicator("name")}
                      </th>
                      <th>COLORS</th>
                      <th style={thStyle("orientation")} onClick={() => handleSort("orientation")}>
                        ORIENTATION{sortIndicator("orientation")}
                      </th>
                      <th>SLIDESHOWS</th>
                      <th style={thStyle("slide_duration")} onClick={() => handleSort("slide_duration")}>
                        DURATION{sortIndicator("slide_duration")}
                      </th>
                      <th>BAR LOGO</th>
                      <th>EXAMPLE</th>
                      <th>ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.id}>
                        <td style={{ verticalAlign: "middle" }}>{row.name}</td>
                        <td style={{ verticalAlign: "middle" }}>
                          <div style={{ display: "flex", gap: 4 }}>
                            {[row.color1, row.color2, row.color3, row.color_bkg].map((c, i) => (
                              <div
                                key={i}
                                title={c}
                                style={{
                                  width: 20,
                                  height: 20,
                                  borderRadius: 3,
                                  border: "1px solid #ddd",
                                  background: c,
                                  flexShrink: 0,
                                }}
                              />
                            ))}
                          </div>
                        </td>
                        <td style={{ verticalAlign: "middle" }}>{row.orientation}</td>
                        <td style={{ verticalAlign: "middle", fontSize: 11 }}>
                          <div>PPS: {row.pps_slideshow ? "Yes" : "No"}</div>
                          <div>Video: {row.video_slideshow ? "Yes" : "No"}</div>
                        </td>
                        <td style={{ verticalAlign: "middle" }}>{row.slide_duration} sec</td>
                        <td style={{ verticalAlign: "middle" }}>{row.bar_logo_type}</td>
                        <td style={{ verticalAlign: "middle" }}>
                          {row.example_url ? (
                            <img
                              src={row.example_url}
                              alt="Example"
                              style={{ maxWidth: 80, maxHeight: 50, objectFit: "contain" }}
                            />
                          ) : (
                            <span style={{ opacity: 0.5, fontSize: 11 }}>No image</span>
                          )}
                        </td>
                        <td style={{ verticalAlign: "middle" }}>
                          <div className="d-flex" style={{ gap: 6 }}>
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => openEdit(row._raw)}
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={async () => {
                                if (window.confirm(`Delete "${row.name}"?`)) {
                                  await api.endpoints.deletePoster(row._raw.id);
                                  await loadPosters();
                                }
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {modal.open && (
        <PosterModal
          key={modal.mode === "edit" ? modal.poster?.id : "create"}
          mode={modal.mode}
          poster={modal.poster}
          systemFonts={systemFonts}
          posterPresets={posterPresets}
          onClose={closeModal}
          onSave={handleSavePoster}
        />
      )}
    </div>
  );
};

export default Posters;
